import {
  TOKEN_KEY,
  REFRESH_TOKEN_KEY,
  USER_SESSION_KEY,
} from "../api/authKeys";

const NATIVE_AUTH_KEYS = [TOKEN_KEY, REFRESH_TOKEN_KEY, USER_SESSION_KEY];
const NATIVE_STORAGE_TIMEOUT_MS = 1200;

function withTimeout(promise, ms = NATIVE_STORAGE_TIMEOUT_MS) {
  return Promise.race([
    promise,
    new Promise((resolve) => {
      window.setTimeout(resolve, ms);
    }),
  ]);
}

function isNativePlatform() {
  try {
    return Boolean(window.Capacitor?.isNativePlatform?.());
  } catch {
    return false;
  }
}

async function getNativePreferences() {
  if (!isNativePlatform()) return null;
  try {
    const { Preferences } = await import("@capacitor/preferences");
    return Preferences;
  } catch {
    return null;
  }
}

async function persistNative(key, value) {
  try {
    await withTimeout(
      (async () => {
        const Preferences = await getNativePreferences();
        if (!Preferences) return;
        if (value == null) {
          await Preferences.remove({ key });
          return;
        }
        await Preferences.set({ key, value });
      })(),
    );
  } catch {
    // Native persistence is best-effort — localStorage remains the runtime source.
  }
}

function storageKey(key) {
  try {
    // eslint-disable-next-line global-require
    if (require("../config/demoMode").isDemoMode()) {
      return `demo:${key}`;
    }
  } catch {
    // ignore
  }
  return key;
}

export function getAuthValue(key) {
  return localStorage.getItem(storageKey(key));
}

export function setAuthValue(key, value) {
  const scoped = storageKey(key);
  if (value == null) {
    localStorage.removeItem(scoped);
    persistNative(scoped, null);
    return;
  }
  localStorage.setItem(scoped, value);
  persistNative(scoped, value);
}

export function removeAuthValue(key) {
  const scoped = storageKey(key);
  localStorage.removeItem(scoped);
  persistNative(scoped, null);
}

export function clearAuthStorage() {
  NATIVE_AUTH_KEYS.forEach((key) => {
    removeAuthValue(key);
    // Also clear the other scope so switching demo ↔ prod never reuses JWTs.
    localStorage.removeItem(key);
    localStorage.removeItem(`demo:${key}`);
    persistNative(key, null);
    persistNative(`demo:${key}`, null);
  });
}

/**
 * If the WebView localStorage was emptied (Android process death / WebView
 * update) but Capacitor Preferences still has the session, copy it back
 * before AuthContext decides whether the employee is logged in.
 */
export async function hydrateAuthStorage() {
  try {
    await withTimeout(
      (async () => {
        const Preferences = await getNativePreferences();
        if (!Preferences) return;
        await Promise.all(
          NATIVE_AUTH_KEYS.map(async (key) => {
            if (localStorage.getItem(key)) return;
            const { value } = await Preferences.get({ key });
            if (value) localStorage.setItem(key, value);
          }),
        );
      })(),
    );
  } catch {
    // Hydration failure must not block app start.
  }
}
